import { useContext, useState, useEffect } from 'react';
import { gql } from '@apollo/client';

import { AppContext } from '../../App.context';
import { Db } from '../../services/Db';
import { KV } from '../../services/kv/KV';
import { Issue, getAccessToken, getImagesFromIssue, clone, getUserRole, mapIssuesNonImplementedFeatures } from '../../constants';
import config from '../../config';

export function useGetIssues(): GetIssuesResponse {
    const {
        appKey,
        apolloClient,
        onlineStatus,
        sw,
        db,
        kv
    } = useContext(AppContext);
    const [issues, setIssues] = useState<Array<Issue>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    useEffect(() => {
        if (!apolloClient) {
            return;
        }
        if (onlineStatus === null) {
            return;
        }
        if (isLoading === false) {
            return;
        }

        if (!db) {
            return;
        }

        const props = {
            apolloClient,
            appKey,
            onlineStatus,
            sw,
            db,
            kv
        };

        getIssues(props)
            .then((issues) => {
                setIssues(issues);
            })
            .catch((err) => {
                console.error(err);
                setError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [apolloClient, onlineStatus, isLoading, sw]);
    return {
        issues,
        isLoading,
        error
    }
}

async function getIssues(args: GetIssuesArgs): Promise<Array<Issue>> {
    const { onlineStatus, db } = args;
    if (onlineStatus) {
        const issues = await getRemoteIssues(args);

        console.log(`Total of issues:`, issues.length);

        await db.saveIssues(issues)

        return issues.sort(sortIssues);
    }

    return (await db.getIssues()).sort(sortIssues);
}

function sortIssues(a: Issue, b: Issue) {
    if (a.releaseDate && b.releaseDate) {
        return new Date(`${b.releaseDate}`).getTime() -
            new Date(`${a.releaseDate}`).getTime();
    }
    return new Date(`${b.updated}`).getTime() - new Date(`${a.updated}`).getTime();
}

async function getRemoteIssues(args: GetIssuesArgs) {
    const { apolloClient, appKey, sw, kv } = args;
    const fetchPolicyKey = 'remote-issues';

    const fetchPolicy = kv.get(fetchPolicyKey, 'no-cache');
    if (!kv.has(fetchPolicyKey)) {
        kv.set(
            fetchPolicyKey,
            'cache-first',
            config?.app?.cache?.ttl?.getAllIssues ?? undefined,
        );
    }

    const headers = {
        'app-key': appKey,
        Authorization: getAccessToken(),
        'app-user-role': getUserRole(),
    }
    const result = await apolloClient.query({
        query: GET_ISSUES,
        fetchPolicy,
        headers
    });

    let { issues } = clone(result.data);
    if (issues['__typename']) {
        delete issues['__typename'];
    }

    // TODO Remove this to support features
    issues = issues.map(mapIssuesNonImplementedFeatures);

    if (sw) {
        const images: Set<string> = new Set();
        getImagesFromIssue(images, issues);
        if (images.size) {
            sw.postMessage({
                type: 'DOWNLOAD_BACKGROUND_IMAGES',
                payload: {
                    images: [...images],
                },
            });
        }
    }

    return issues;
}

interface GetIssuesResponse {
    issues: Array<Issue>;
    isLoading: boolean;
    error?: Error | null;
}

interface GetIssuesArgs {
    appKey: string;
    apolloClient: any;
    onlineStatus: boolean;
    db: Db;
    kv: KV;
    sw: ServiceWorker | null;
}

export const GET_ISSUES = gql`
query Issues {
        issues {
            id
            name
            description
            cover
            externalUrl
            price
            index
            slug
            currency
            data
            updated
            created
            releaseDate
            isPurchased
            isPaid
        }	}`

// TODO Revert back to support features
export const HACK_GET_ISSUES = gql`
query Issues {
        issues {
            id
            name
            description
            cover
            externalUrl
            price
            index
            slug
            features
            currency
            isFree
            data
            updated
            created
            releaseDate
            isPurchased
            isPaid
        }	}`

